import {
    createAsyncThunk,
    createReducer,
    createSelector,
} from '@reduxjs/toolkit';
import z from 'zod';
import { api } from '$api/api';
import type { RootState } from '$state/store';
import { withState } from '$state/utils';
import { integrationHealthy } from '$state/concerns/integrations';
import { obj, str, phone } from '$utils';
import type { DentallyPatient } from '$state/types/patients';

// types

// This boilerplate converts snake_case keys to camelCase using Zod transforms.
// This could be generalised in the future to allow JSON responses to be in
// snake case and converted into camel case automatically.
//
// For requests, it would work in a similar way but in reverse.
//
// Doing this on the API would be a blessing.

const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type Literal = z.infer<typeof literalSchema>;
type Json = Literal | { [key: string]: Json } | Json[];
const jsonSchema: z.ZodType<Json> = z.lazy(() =>
    z.union([literalSchema, z.array(jsonSchema), z.record(jsonSchema)]),
);

const transformKeys = (x: Json): Json => {
    if (typeof x !== 'object' || x === null) {
        return x;
    }

    if (Array.isArray(x)) {
        return x.map(transformKeys);
    }

    const camelCased = obj.mapKeys(x, str.camel);

    return obj.mapValues(camelCased, transformKeys);
};

const fromSnakeCase = () => jsonSchema.transform(transformKeys);

const PatientDetailsSchema = fromSnakeCase().pipe(
    z.object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        middleName: z.string().nullable(),
        emailAddress: z.string().email().nullable(),
        phone: z.string().nullable(),
        //TODO: add localisation - ticket #93552
        dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    }),
);

export type PatientDetails = z.infer<typeof PatientDetailsSchema>;

const InvoiceSchema = z.object({
    id: z.number(),
    date: z.string(),
    amount: z.string(),
});

const TreatmentSchema = z.object({
    id: z.number(),
    name: z.string(),
    amount: z.string(),
    quantity: z.string(),
});

const PatientFinancialsSchema = fromSnakeCase().pipe(
    z.object({
        dentallyId: z.number(),

        invoices: z.object({
            unpaid: z.array(InvoiceSchema),
            paid: z.array(InvoiceSchema),
        }),

        uninvoicedTreatments: z.array(TreatmentSchema),

        total: z.object({
            unpaid: z.string(),
            paid: z.string(),
        }),
    }),
);

export type PatientFinancials = z.infer<typeof PatientFinancialsSchema>;

const SyncingStateSchema = z.object({
    state: z.literal('syncing'),
    details: PatientDetailsSchema,
});

type SyncingState = z.infer<typeof SyncingStateSchema>;

const SyncedStateSchema = z.object({
    state: z.literal('synced'),
    details: PatientDetailsSchema,
    financials: PatientFinancialsSchema,
});

export type SyncedState = z.infer<typeof SyncedStateSchema>;

export const PatientStateSchema = z.discriminatedUnion('state', [
    z.object({ state: z.literal('unlinked') }),
    SyncingStateSchema,
    SyncedStateSchema,
]);

type DisabledState = { state: 'disabled' };
type IdleState = { state: 'idle' };
type LoadingState = { state: 'loading' };
type LinkingState = { state: 'linking' };

export type LinkedState = SyncingState | SyncedState;

export type PatientLinkState = z.infer<typeof PatientStateSchema>;

export type PatientState =
    | DisabledState
    | IdleState
    | LoadingState
    | LinkingState
    | { state: 'error'; error: string }
    | PatientLinkState;

interface PatientStateMap {
    [id: string]: PatientState;
}

export const DentallyPatientAddFormSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    first_name: z.string().min(1, 'First name is required'),
    last_name: z.string().min(1, 'Last name is required'),
    email_address: z.string().email('Invalid email address').optional(),
    mobile_phone: z.string().refine((value) => {
        return phone.is(value)
    }, {
        message: 'Invalid phone number',
    }),
    date_of_birth: z.string()
        .min(1, 'Date of birth is required')
        .regex(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, {
            message: 'Date must be in DD/MM/YYYY format'
        })
        .refine((date) => {
            const [day, month, year] = date.split('/').map(Number);
            const parsedDate = new Date(year, month - 1, day);
            return parsedDate.getDate() === day &&
                parsedDate.getMonth() === month - 1 &&
                parsedDate.getFullYear() === year;
        }, {
            message: 'Invalid date'
        })
        .refine((date) => {
            const [day, month, year] = date.split('/').map(Number);
            const parsedDate = new Date(year, month - 1, day);
            return parsedDate < new Date();
        }, {
            message: 'Date of birth should be in the past'
        }),
    gender: z.enum(['male', 'female'], {
        required_error: 'Gender is required',
        invalid_type_error: 'Gender must be either male or female'
    }),
    ethnicity: z.string().min(1, 'Ethnicity is required'),
    address_line_1: z.string().min(1, 'Address is required'),
    address_line_2: z.string().optional(),
    postcode: z.string().min(1, 'Postcode is required'),
    payment_plan_id: z.string().min(1, 'Payment plan is required')
});

export type DentallyPatientAddFormType = z.infer<typeof DentallyPatientAddFormSchema>;

// State

export const get = createAsyncThunk<PatientState, string>(
    'dentally/patients/get',
    (id: string, ThunkAPI) => {
        const state = ThunkAPI.getState() as RootState;
        const healthy = integrationHealthy(state, 'dentally');

        if (!healthy) {
            return Promise.resolve({ state: 'disabled' });
        }

        return api.get(`/dentally/patients/${id}`).then(({ data }) => PatientStateSchema.parse(data));
    },
);

interface LinkArgs {
    patientId: string;
    dentallyId: number;
}

interface CreateArgs {
    patientId: string;
    values: DentallyPatient | null
}

export const create = createAsyncThunk<void, CreateArgs>(
    'dentally/patients/create',
    async (args, ThunkAPI) => {
        await api.post(`/dentally/patients/${args.patientId}`, {
            ...args.values
        });

        await ThunkAPI.dispatch(get(args.patientId));
    },
);

export const link = createAsyncThunk<void, LinkArgs>(
    'dentally/patients/link',
    async (args, ThunkAPI) => {
        await api.post('/dentally/links', {
            patient_id: args.patientId,
            id: args.dentallyId,
        });

        await ThunkAPI.dispatch(get(args.patientId));
    },
);

export const unlink = createAsyncThunk<void, string>(
    'dentally/patients/unlink',
    async (id: string) => {
        await api.delete(`/dentally/links/${id}`);
    },
);

const initialState: PatientStateMap = {};

export default createReducer(initialState, (builder) => {
    builder.addCase(get.pending, (state, action) => {
        if (state[action.meta.arg]?.state !== 'idle') {
            return;
        }

        state[action.meta.arg] = { state: 'loading' };
    });

    builder.addCase(get.fulfilled, (state, action) => {
        state[action.meta.arg] = action.payload;
    });

    builder.addCase(get.rejected, (state, action) => {
        state[action.meta.arg] = {
            state: 'error',
            error: action.error.message ?? 'Unknown error',
        };
    });

    builder.addCase(link.pending, (state, action) => {
        state[action.meta.arg.patientId] = { state: 'linking' };
    });

    builder.addCase(unlink.pending, (state, action) => {
        state[action.meta.arg] = { state: 'unlinked' };
    });
});

// Selectors

const selectState = (state: RootState) => state.dentally.patients;

export const selectPatient = createSelector(
    [
        selectState,
        (_: RootState, id: string) => id,
        withState(integrationHealthy, 'dentally'),
    ],
    (state, id, healthy) => {
        if (!healthy) {
            return { state: 'disabled' } as const;
        }

        return state[id] ?? ({ state: 'idle' } as const);
    },
);

export const disabled = (state: PatientState): state is DisabledState =>
    state.state === 'disabled';

export const idle = (state: PatientState): state is IdleState =>
    state.state === 'idle';

export const unlinked = (state: PatientState): state is { state: 'unlinked' } =>
    state.state === 'unlinked';

export const loading = (state: PatientState): state is LoadingState =>
    state.state === 'loading';

export const linking = (state: PatientState): state is LinkingState =>
    state.state === 'linking';

export const linked = (state: PatientState): state is LinkedState =>
    state.state === 'syncing' || state.state === 'synced';

export const syncing = (state: PatientState): state is SyncingState =>
    state.state === 'syncing';

export const synced = (state: PatientState): state is SyncedState =>
    state.state === 'synced';

export const details = ({ details }: LinkedState) => details;

export const financials = ({ financials }: SyncedState) => financials;
